import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWishlist } from '../../../context/WishlistContext';
import QuickViewModal from '../../../components/ui/QuickViewModal';
import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RelatedProducts = ({ products = [], onAddToCart }) => {
  const navigate = useNavigate();
  const wishlist = useWishlist();
  const [quickView, setQuickView] = useState(null);
  // No hardcoded fallback — empty array means render nothing.
  const defaultProducts = [];


  const relatedProducts = products?.length > 0 ? products : defaultProducts;

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars?.push(
        <Icon key={i} name="Star" size={12} className="text-warning fill-current" />
      );
    }

    if (hasHalfStar) {
      stars?.push(
        <Icon key="half" name="StarHalf" size={12} className="text-warning fill-current" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(
        <Icon key={`empty-${i}`} name="Star" size={12} className="text-muted-foreground" />
      );
    }

    return stars;
  };

  const handleAddToCart = (product) => {
    if (onAddToCart) {
      onAddToCart(product);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-heading font-semibold text-foreground">
          You might also like
        </h2>
        <Link
          to="/product-catalog"
          className="text-sm text-primary hover:underline font-medium">

          View all products
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {relatedProducts?.map((product) => {
          const discount = product?.originalPrice ?
          Math.round((product?.originalPrice - product?.price) / product?.originalPrice * 100) : 0;

          return (
            <div key={product?.id} className="bg-card rounded-2xl border border-border shadow-elevation-1 overflow-hidden group hover:shadow-elevation-2 transition-shadow">
              {/* Product Image */}
              <div className="relative aspect-square overflow-hidden">
                <Link to={`/product-details?id=${product?.id}`}>
                  <Image
                    src={product?.image}
                    alt={product?.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

                </Link>
                
                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col space-y-2">
                  {product?.isNew &&
                  <span className="bg-primary text-primary-foreground text-xs font-medium px-2 py-1 rounded-lg">
                      New
                    </span>
                  }
                  {discount > 0 &&
                  <span className="bg-error text-error-foreground text-xs font-medium px-2 py-1 rounded-lg">
                      -{discount}%
                    </span>
                  }
                  {!product?.inStock &&
                  <span className="bg-muted text-muted-foreground text-xs font-medium px-2 py-1 rounded-lg">
                      Out of Stock
                    </span>
                  }
                </div>

                {/* Quick Actions */}
                <div className="absolute top-3 right-3 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); wishlist.toggle(product); }}
                    aria-label={wishlist.has(product?.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                    className="w-8 h-8 bg-background/80 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-background transition-colors">
                    <Icon name="Heart" size={16} className={wishlist.has(product?.id) ? 'text-error fill-current' : ''} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickView(product); }}
                    aria-label="Quick view"
                    className="w-8 h-8 bg-background/80 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-background transition-colors">
                    <Icon name="Eye" size={16} />
                  </button>
                </div>
              </div>
              {/* Product Info */}
              <div className="p-4 space-y-3">
                <div>
                  <p className="text-xs font-medium text-primary mb-1">{product?.brand}</p>
                  <Link to={`/product-details?id=${product?.id}`}>
                    <h3 className="font-medium text-foreground hover:text-primary transition-colors line-clamp-2">
                      {product?.name}
                    </h3>
                  </Link>
                </div>

                {/* Rating */}
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    {renderStars(product?.rating)}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    ({product?.reviewCount})
                  </span>
                </div>

                {/* Pricing */}
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-foreground">
                    ${product?.price?.toFixed(2)}
                  </span>
                  {product?.originalPrice &&
                  <span className="text-sm text-muted-foreground line-through">
                      ${product?.originalPrice?.toFixed(2)}
                    </span>
                  }
                </div>

                {/* Add to Cart Button */}
                <Button
                  variant={product?.inStock ? "default" : "outline"}
                  size="sm"
                  fullWidth
                  disabled={!product?.inStock}
                  onClick={() => handleAddToCart(product)}
                  iconName={product?.inStock ? "ShoppingCart" : "AlertCircle"}
                  iconPosition="left">

                  {product?.inStock ? 'Add to Cart' : 'Out of Stock'}
                </Button>
              </div>
            </div>);

        })}
      </div>
    </div>);

};

export default RelatedProducts;